import { Box, Stack, useTheme } from '@mui/material';
import { RefObject, useCallback, useEffect, useMemo } from 'react';

import { useActiveTab } from '@/components/navigation/NavigationTabs/useActiveTab';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useOptionalDocumentChatContext } from '@/modules/chat/contexts/DocumentChatProvider';
import { DocumentChatProvider } from '@/modules/chat/contexts/DocumentChatProvider';
import { EntityDocumentViewer_FilePageFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { DocumentDetailsViewOptions } from '@/modules/documents/documents.constants';
import { VIEWER_ONLY_DOCUMENT_TYPES } from '@/modules/documents/documentTypePolicies';
import { EditSummaryTrowser } from '@/modules/documents/EditSummaryTrowser/EditSummaryTrowser';
import { ManageDocumentAmendments } from '@/modules/documents/ManageDocumentAmendments/ManageDocumentAmendments';
import { SelectableDocument } from '@/modules/documents/ManageDocumentAmendments/types';
import { RegenerateSummaryModal } from '@/modules/documents/RegenerateSummaryModal/RegenerateSummaryModal';
import { useHouseholdChatReplacement } from '@/modules/household/chat/hooks/useHouseholdChatReplacement';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import type { PDFSource } from '@/modules/pdf/PDFViewer';
import { AnnotatedPDFViewerProvider } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.provider';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useIsDocumentWorkflowTrackerInProgress } from '@/modules/workflowTrackers/hooks/useIsDocumentWorkflowTrackerInProgress';
import { DocumentViewerContainer } from '@/pages/DocumentDetailsPage/components/DocumentViewer/DocumentViewerContainer';
import { type DocumentType, WorkflowTrackerKind } from '@/types/schema';

import { AboutSection } from './components';
import { useDocumentDetails } from './contexts/DocumentDetails.context';
import { DocumentDetailsProvider } from './contexts/DocumentDetails.provider';
import {
  AboutLoadingState,
  DocumentDetailsPageVariant,
  SummaryLoadingState,
} from './DocumentDetailsPage.types';
import { useAssociatedDocuments } from './hooks/useAssociatedDocuments';
import { useDocumentDetailsData } from './hooks/useDocumentDetailsData';
import { useDocumentId } from './hooks/useDocumentId';
import { useErrorFeedback } from './hooks/useErrorFeedback';
import { usePageLoadingBridge } from './hooks/usePageLoadingBridge';
import { useSummaryModal } from './hooks/useSummaryModal';
import { useSummaryProcessing } from './hooks/useSummaryProcessing';
import { useViewerHeight } from './hooks/useViewerHeight';

interface DocumentDetailsPageProps {
  variant: DocumentDetailsPageVariant;
}

function DocumentDetailsPageInner() {
  const {
    documentId,
    documentSummary,
    associatedDocuments,
    isEditSummaryTrowserOpen,
    closeEditSummaryTrowser,
  } = useDocumentDetails();

  return (
    <>
      {/* We need to reset the state of the form in the trowser upon open because the editor and form
      both maintain their own state and if they differ the trowser content and page content might differ */}
      {isEditSummaryTrowserOpen && (
        <EditSummaryTrowser
          isOpen={isEditSummaryTrowserOpen}
          onClose={closeEditSummaryTrowser}
          documentId={documentId}
          documentSummary={documentSummary ?? undefined}
          additionalDocuments={associatedDocuments}
        />
      )}
    </>
  );
}

interface DocumentViewerContainerWrapperProps {
  documentId: string;
  source: PDFSource | null;
  documentViewerHeight: string;
  documentFilePages: EntityDocumentViewer_FilePageFragment[];
  aiEnabled: boolean;
  activeTab: DocumentDetailsViewOptions;
  setActiveTab: (tab: DocumentDetailsViewOptions) => void;
  viewerRef: RefObject<HTMLDivElement | null>;
  summaryLoadingState: SummaryLoadingState;
  onGenerateAiSummary: () => void;
  documentType?: DocumentType;
  associatedDocuments?: SelectableDocument[];
}

function DocumentViewerContainerWrapper({
  documentId,
  source,
  documentViewerHeight,
  documentFilePages,
  aiEnabled,
  activeTab,
  setActiveTab,
  viewerRef,
  summaryLoadingState,
  onGenerateAiSummary,
  documentType,
  associatedDocuments,
}: DocumentViewerContainerWrapperProps) {
  const { householdId } = useHouseholdDetailsContext();
  const { openEditSummaryTrowser } = useDocumentDetails();
  const documentChatContext = useOptionalDocumentChatContext();
  const { isHouseholdChatReplacementEnabled } = useHouseholdChatReplacement();

  const isAIEnabled = useAICapabilitiesEnabled();
  const canEditHouseholdData = useCanEditHouseholdData();
  const isOcrInProgress = useIsDocumentWorkflowTrackerInProgress(
    documentId,
    WorkflowTrackerKind.DocumentOcr
  );

  const handleOpenChatWithSummary = useCallback(async () => {
    if (!householdId) return;
    if (!documentChatContext) return;

    // Open the edit summary trowser and chat
    openEditSummaryTrowser();
    documentChatContext.openChat();
  }, [householdId, documentChatContext, openEditSummaryTrowser]);

  // Only show the button if all conditions are met:
  // - AI capabilities are enabled
  // - User can edit household data
  // - DocumentChatContext is available
  // - Document OCR workflow has completed
  const showOpenChatButton =
    isAIEnabled &&
    canEditHouseholdData &&
    !!documentChatContext &&
    !isHouseholdChatReplacementEnabled &&
    !isOcrInProgress;

  return (
    <DocumentViewerContainer
      documentId={documentId}
      source={source}
      documentViewerHeight={documentViewerHeight}
      documentFilePages={documentFilePages}
      aiEnabled={aiEnabled}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      viewerRef={viewerRef}
      summaryLoadingState={summaryLoadingState}
      onGenerateAiSummary={onGenerateAiSummary}
      documentType={documentType}
      associatedDocuments={associatedDocuments}
      onOpenChatWithSummary={
        showOpenChatButton ? handleOpenChatWithSummary : undefined
      }
    />
  );
}

export function DocumentDetailsPage({ variant }: DocumentDetailsPageProps) {
  const theme = useTheme();
  const aiEnabled = useAICapabilitiesEnabled();

  const { documentId } = useDocumentId();
  const isAssociatingDocuments = useIsDocumentWorkflowTrackerInProgress(
    documentId,
    WorkflowTrackerKind.DocumentExtraction
  );

  const { activeTab, setActiveTab } = useActiveTab<DocumentDetailsViewOptions>({
    defaultTab: 'summary',
    filterSearchParam: 'documentView',
  });

  const {
    document,
    source,
    filePages,
    summaries,
    householdId,
    loading: queryLoading,
    refetch,
  } = useDocumentDetailsData(documentId);

  const documentType = document?.type;

  // Viewer-only types (e.g. presentations, binders) always show the PDF viewer.
  // Force the page onto the document tab so the tab state, layout, and viewer
  // sizing all agree instead of carrying viewer-only exceptions downstream.
  const isViewerOnly =
    documentType != null && VIEWER_ONLY_DOCUMENT_TYPES.includes(documentType);
  const effectiveActiveTab = isViewerOnly ? 'document' : activeTab;

  useEffect(() => {
    // Viewer-only documents never render the tab switcher, so users cannot
    // intentionally move away from the document view. This only normalizes a
    // stale `documentView=summary` URL once the document type resolves.
    if (isViewerOnly && activeTab !== 'document') {
      setActiveTab('document');
    }
  }, [activeTab, isViewerOnly, setActiveTab]);

  const { associatedDocuments } = useAssociatedDocuments(documentId);
  const { viewerRef, height: documentViewerHeight } = useViewerHeight({
    aiEnabled,
    activeTab: effectiveActiveTab,
    theme,
  });

  const { isGenerating, onStartGenerating, setIsGeneratingAiSummaryOverride } =
    useSummaryProcessing(documentId, householdId, refetch);

  const {
    isOpen: isSummaryModalOpen,
    open: openSummaryModal,
    close: closeSummaryModal,
    transitionOnNewSummary,
    setTransitionOnNewSummary,
  } = useSummaryModal();

  const { aboutLoadingState, summaryLoadingState, error } =
    usePageLoadingBridge({
      document: document ?? null,
      queryLoading,
      isGenerating,
    });

  useErrorFeedback({ error, documentId });

  const providerValue = useMemo(
    () => ({
      documentId,
      source,
      documentFilePages: filePages,
      associatedDocuments,
      summaries,
      setIsGeneratingAiSummaryOverride,
      transitionToNewSummaryWhenAvailable: transitionOnNewSummary,
      setTransitionToNewSummaryWhenAvailable: setTransitionOnNewSummary,
    }),
    [
      documentId,
      source,
      filePages,
      associatedDocuments,
      summaries,
      setIsGeneratingAiSummaryOverride,
      transitionOnNewSummary,
      setTransitionOnNewSummary,
    ]
  );

  return (
    <>
      <Stack height="100%">
        <DocumentDetailsProvider {...providerValue}>
          <AnnotatedPDFViewerProvider
            documentId={documentId}
            showCitations
            showCopyCitationsButton
          >
            <DocumentChatProvider documentId={documentId}>
              <Stack
                sx={{
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 2,
                }}
              >
                <Stack
                  spacing={2}
                  sx={{
                    width: { xs: '100%', lg: 420 },
                    flexShrink: 0,
                  }}
                >
                  {isAssociatingDocuments && (
                    <Callout severity="success">
                      Identifying associated entities and documents
                    </Callout>
                  )}
                  <AboutSection
                    document={document}
                    variant={variant}
                    aboutLoadingState={aboutLoadingState}
                  />
                  {aboutLoadingState === AboutLoadingState.Done && document && (
                    // Reset pending edits when navigation reuses this page for
                    // another document.
                    <ManageDocumentAmendments
                      key={document.id}
                      documentId={document.id}
                      currentDocumentType={document.type}
                      associatedDocuments={associatedDocuments}
                      onSummaryStarted={onStartGenerating}
                    />
                  )}
                </Stack>
                <Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <DocumentViewerContainerWrapper
                      documentId={documentId}
                      source={source}
                      documentViewerHeight={documentViewerHeight}
                      documentFilePages={filePages}
                      aiEnabled={aiEnabled}
                      activeTab={effectiveActiveTab}
                      setActiveTab={setActiveTab}
                      viewerRef={viewerRef}
                      summaryLoadingState={summaryLoadingState}
                      onGenerateAiSummary={openSummaryModal}
                      documentType={documentType}
                      associatedDocuments={associatedDocuments}
                    />
                  </Box>
                </Stack>
              </Stack>
              <DocumentDetailsPageInner />
            </DocumentChatProvider>
          </AnnotatedPDFViewerProvider>
        </DocumentDetailsProvider>
      </Stack>
      {isSummaryModalOpen && documentType && (
        <RegenerateSummaryModal
          heading="Generate summary"
          isOpen={isSummaryModalOpen}
          onClose={closeSummaryModal}
          documentId={documentId}
          documentType={documentType}
          openedFromAddNew={true}
          onSuccess={onStartGenerating}
          setTransitionToNewSummaryWhenAvailable={setTransitionOnNewSummary}
        />
      )}
    </>
  );
}
